import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { addWorkoutVideo } from '../services/firestore';

const Admin = () => {
    const { user } = useAuth();
    const { darkMode } = useTheme();

    const ADMIN_EMAIL = 'emailjaspertan@gmail.com';

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'strength',
        difficulty: 'beginner',
        duration: '',
        equipment: 'none',
    });

    const [videoFile, setVideoFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Cloudinary configuration
    const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dwgbc05ja/upload';
    const CLOUDINARY_UPLOAD_PRESET = 'workout_videos'; // You'll create this in Cloudinary dashboard

    // Check if user is admin
    if (!user || user.email !== ADMIN_EMAIL) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-8 text-center`}>
                    <div className="text-6xl mb-4">🔒</div>
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                        Access Denied
                    </h2>
                    <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        This page is restricted to administrators only.
                    </p>
                </div>
            </div>
        );
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleVideoFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (limit to 100MB)
            if (file.size > 100 * 1024 * 1024) {
                toast.error('Video file must be less than 100MB');
                return;
            }

            // Check file type
            if (!file.type.startsWith('video/')) {
                toast.error('Please select a valid video file');
                return;
            }

            setVideoFile(file);
            toast.success(`Video selected: ${file.name}`);
        }
    };

    const uploadToCloudinary = async (file, resourceType = 'video') => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
        formData.append('resource_type', resourceType);

        try {
            const response = await fetch(CLOUDINARY_URL.replace('/upload', `/${resourceType}/upload`), {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const data = await response.json();
            return data.secure_url;
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            throw error;
        }
    };

    const deleteFromCloudinary = async (videoUrl, thumbnailUrl) => {
        try {
            // Extract public_id from Cloudinary URLs
            const extractPublicId = (url) => {
                if (!url) return null;
                const parts = url.split('/');
                const uploadIndex = parts.indexOf('upload');
                if (uploadIndex === -1) return null;

                // Get everything after 'upload/v{version}/'
                const pathParts = parts.slice(uploadIndex + 2);
                const fullPath = pathParts.join('/');

                // Remove file extension
                return fullPath.replace(/\.[^/.]+$/, '');
            };

            const videoPublicId = extractPublicId(videoUrl);
            const thumbnailPublicId = extractPublicId(thumbnailUrl);

            const deletePromises = [];

            // Delete video from Cloudinary
            if (videoPublicId) {
                const videoDeleteUrl = `https://api.cloudinary.com/v1_1/dwgbc05ja/video/destroy`;
                deletePromises.push(
                    fetch(videoDeleteUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            public_id: videoPublicId,
                            upload_preset: CLOUDINARY_UPLOAD_PRESET,
                        }),
                    })
                );
            }

            // Delete thumbnail from Cloudinary (if different from video)
            if (thumbnailPublicId && thumbnailPublicId !== videoPublicId) {
                const thumbnailDeleteUrl = `https://api.cloudinary.com/v1_1/dwgbc05ja/image/destroy`;
                deletePromises.push(
                    fetch(thumbnailDeleteUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            public_id: thumbnailPublicId,
                            upload_preset: CLOUDINARY_UPLOAD_PRESET,
                        }),
                    })
                );
            }

            if (deletePromises.length > 0) {
                await Promise.all(deletePromises);
                console.log('Files deleted from Cloudinary successfully');
            }

        } catch (error) {
            console.error('Error deleting from Cloudinary:', error);
            // Don't throw error - we still want to delete from Firestore even if Cloudinary fails
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!videoFile) {
            toast.error('Please select a video file');
            return;
        }

        if (!formData.name.trim()) {
            toast.error('Please enter a workout name');
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        try {
            // Upload video to Cloudinary
            toast.info('Uploading video... This may take a while for large files.');
            setUploadProgress(50);
            const videoUrl = await uploadToCloudinary(videoFile, 'video');

            // Generate thumbnail image from video (this creates a proper image file)
            const thumbnailUrl = videoUrl.replace('/video/upload/', '/video/upload/so_0,w_400,h_300,c_fill,f_jpg/');

            setUploadProgress(80);

            // Save to Firestore
            toast.info('Saving workout data...');
            const workoutData = {
                ...formData,
                videoUrl,
                thumbnailUrl, // This is now a proper image URL
                fileSize: videoFile.size,
                fileName: videoFile.name,
                rating: 0,
            };

            await addWorkoutVideo(workoutData);

            setUploadProgress(100);
            toast.success('Workout video uploaded successfully! 🎉');

            // Reset form
            setFormData({
                name: '',
                description: '',
                category: 'strength',
                difficulty: 'beginner',
                duration: '',
                equipment: 'none',
            });
            setVideoFile(null);

            // Reset file input
            document.getElementById('video-input').value = '';

        } catch (error) {
            console.error('Error uploading workout:', error);
            toast.error(`Upload failed: ${error.message}`);
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6`}>
                🎥 Admin - Add Workout Video
            </h2>

            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                Workout Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                    : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                    } rounded-md focus:outline-none focus:ring-2`}
                                placeholder="e.g., Basic Push-ups"
                            />
                        </div>

                        <div>
                            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                Duration
                            </label>
                            <input
                                type="text"
                                name="duration"
                                value={formData.duration}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                    : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                    } rounded-md focus:outline-none focus:ring-2`}
                                placeholder="e.g., 5:30"
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={3}
                            className={`w-full px-3 py-2 border ${darkMode
                                ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                } rounded-md focus:outline-none focus:ring-2`}
                            placeholder="Describe the workout..."
                        />
                    </div>

                    {/* Category and Difficulty */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                Category
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                    : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                    } rounded-md focus:outline-none focus:ring-2`}
                            >
                                <option value="strength">Strength</option>
                                <option value="cardio">Cardio</option>
                                <option value="flexibility">Flexibility</option>
                                <option value="hiit">HIIT</option>
                            </select>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                Difficulty
                            </label>
                            <select
                                name="difficulty"
                                value={formData.difficulty}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                    : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                    } rounded-md focus:outline-none focus:ring-2`}
                            >
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>

                        <div>
                            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                Equipment
                            </label>
                            <select
                                name="equipment"
                                value={formData.equipment}
                                onChange={handleInputChange}
                                className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white focus:ring-blue-400'
                                    : 'border-gray-300 bg-white text-gray-900 focus:ring-blue-500'
                                    } rounded-md focus:outline-none focus:ring-2`}
                            >
                                <option value="none">No Equipment</option>
                                <option value="dumbbells">Dumbbells</option>
                                <option value="resistance-bands">Resistance Bands</option>
                                <option value="yoga-mat">Yoga Mat</option>
                                <option value="kettlebell">Kettlebell</option>
                                <option value="barbell">Barbell</option>
                            </select>
                        </div>
                    </div>

                    {/* File Uploads */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                Video File * (MP4 recommended, max 100MB)
                            </label>
                            <input
                                id="video-input"
                                type="file"
                                accept="video/*"
                                onChange={handleVideoFileChange}
                                className={`w-full px-3 py-2 border ${darkMode
                                    ? 'border-gray-600 bg-gray-700 text-white'
                                    : 'border-gray-300 bg-white text-gray-900'
                                    } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            {videoFile && (
                                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Selected: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Upload Progress */}
                    {uploading && (
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Upload Progress</span>
                                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{Math.round(uploadProgress)}%</span>
                            </div>
                            <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={uploading}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                        {uploading ? 'Uploading...' : 'Upload Workout Video'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Admin;